// src/modules/app-views/crm/components/CardHeaderTags.jsx
// *******************************************************************************
// CRM CARD HEADER - TAGS ROW: SLA timer
// *******************************************************************************
// Displays the SLA countdown (or elapsed time) for the current board column.
// Moved here from CardHeaderColumn2 so the contact info can occupy column 2.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect } from 'react';
import { Tag, Tooltip } from 'antd';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { IoHandLeftOutline } from 'react-icons/io5';
import dayjs from 'dayjs';
import useZustand from 'hooks/useZustand';
import crmService from '../service';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CrmCardHeaderTags = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { boardUid, columnUid } = props;
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const boards = storedParameters?.modules?.crm?.boards || [];
  const board = boards.find(b => b.uid === boardUid) || null;
  const column = board?.columns?.find(c => c.uid === columnUid) || null;
  const columnSla = column?.automation?.sla;

  const boardBusinessHours = board?.businessHours || null;
  const boardHolidays = board?.holidays || [];

  const slaLimitBusinessMinutes =
    columnSla?.businessTime?.minutes || columnSla?.businessMinutes || 0;
  const slaLimitHours = columnSla?.elapsedTime
    ? (columnSla.elapsedTime.minutes || 0) / 60
    : columnSla?.elapsedHours || 0;

  const hasSla = slaLimitBusinessMinutes > 0 || slaLimitHours > 0;

  const card = props;
  const isFiled = !!props._metadata?.audit?.filedAt;
  const isRescheduled = !!props.attendance?.rescheduledAt && !isFiled;
  const rescheduledTo = props.attendance?.rescheduledTo || null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [elapsedMs, setElapsedMs] = useState(() => crmService.computeElapsedMs(card));
  const [bizSecElapsed, setBizSecElapsed] = useState(() => {
    if (!slaLimitBusinessMinutes) return 0;
    const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
    return crmService.computeBusinessSecondsElapsed(
      crmService.getCardInAt(card),
      schedule,
      boardHolidays
    );
  });
  const [isInBizHours, setIsInBizHours] = useState(() => {
    if (!slaLimitBusinessMinutes) return true;
    const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
    return crmService.checkIsInBusinessHours(schedule, boardHolidays);
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasSla) return;

    const tick = () => {
      setElapsedMs(crmService.computeElapsedMs(card));
      if (slaLimitBusinessMinutes > 0) {
        const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
        const inAt = crmService.getCardInAt(card);
        setBizSecElapsed(crmService.computeBusinessSecondsElapsed(inAt, schedule, boardHolidays));
        setIsInBizHours(crmService.checkIsInBusinessHours(schedule, boardHolidays));
      }
    };

    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnUid, slaLimitBusinessMinutes, slaLimitHours, boardBusinessHours]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Show rescheduled tag regardless of SLA config
  if (isRescheduled) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Tooltip title={translateX('rescheduled_opportunity')} placement="right">
          <Tag icon={<CalendarOutlined />} color="blue" style={{ margin: 0, cursor: 'default' }}>
            {rescheduledTo
              ? dayjs(rescheduledTo).format('DD/MM/YY HH:mm')
              : translateX('rescheduled')}
          </Tag>
        </Tooltip>
      </div>
    );
  }

  if (!hasSla) return null;

  let slaLabel = null;
  let slaColor = '#8c8c8c';
  let slaTooltip = '';
  let slaIcon = <ClockCircleOutlined style={{ fontSize: 13, flexShrink: 0 }} />;

  if (slaLimitBusinessMinutes > 0) {
    const limitSec = slaLimitBusinessMinutes * 60;
    const remainingSec = limitSec - bizSecElapsed;
    const pct = remainingSec / limitSec;
    slaColor = crmService.getSlaColor(pct);
    slaLabel = crmService.formatDurationMs(remainingSec * 1_000);
    slaTooltip = isInBizHours ? translateX('time_running') : translateX('time_paused');
    slaIcon = isInBizHours ? (
      <ClockCircleOutlined style={{ fontSize: 13, flexShrink: 0 }} />
    ) : (
      <IoHandLeftOutline style={{ fontSize: 13, flexShrink: 0 }} />
    );
  } else if (slaLimitHours > 0) {
    const limitMs = slaLimitHours * 3_600_000;
    const remainingMs = limitMs - elapsedMs;
    const pct = remainingMs / limitMs;
    slaColor = crmService.getSlaColor(pct);
    slaLabel = crmService.formatDurationMs(remainingMs);
    slaTooltip = translateX('elapsed_time');
  }

  if (!slaLabel) return null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tooltip placement="right" title={slaTooltip}>
        <div className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
          <span style={{ color: slaColor, display: 'inline-flex', alignItems: 'center' }}>
            {slaIcon}
          </span>
          <span
            className="ml-2 font-weight-semibold"
            style={{ color: slaColor, lineHeight: 1, fontSize: 13 }}
          >
            {slaLabel}
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CrmCardHeaderTags;
